/**
 * 拠点の一覧（issue #124・docs/01 20-3「拠点の一覧」）。
 *
 * ここでできること:
 *  - 拠点を**足す**（一覧のいちばん下に入る）。足すと、会社の設定の「拠点のトーク」で決めた
 *    トーク（拠点のひな形）もサーバが一緒に作る（issue #517）
 *  - 拠点の**名前を変える**
 *
 * 拠点を消すことはできません（所属している方・拠点のトーク・操作履歴がその拠点を指しているため）。
 * 同じ名前の拠点は作れません（名簿のCSVは拠点を名前で指すため。サーバが断ります）。
 *
 * 決定22 に沿って、足す・名前を変えるはどちらも**確認の窓をはさみ**、終わったら結果を出します。
 * 足した・名前を変えたことは操作履歴に残ります（サーバ側で記録）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import type { SiteList, SiteRow } from '../api/types.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { endsWithRequest, withReason } from '../format/sentence.js';

/** 拠点の名前の長さの上限（サーバの SITE_NAME_MAX_LENGTH と同じ）。 */
const SITE_NAME_MAX_LENGTH = 50;

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: string;
}

/** 確認の窓に出しているもの。 */
type Confirming =
  | { kind: 'none' }
  | { kind: 'create'; name: string }
  | { kind: 'rename'; site: SiteRow; name: string };

export function SitesPage(): ReactNode {
  const api = useApi();
  const [sites, setSites] = useState<SiteRow[] | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [newName, setNewName] = useState('');
  /** 名前を変えている行と、入れている名前 */
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [confirming, setConfirming] = useState<Confirming>({ kind: 'none' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<SiteList>('/admin/sites')
      .then((loaded) => {
        if (!cancelled) setSites(loaded.sites);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  function askCreate(): void {
    setResult(null);
    const name = newName.trim();
    if (name === '') {
      setResult({
        tone: 'error',
        message: '拠点を足せませんでした。拠点の名前を入れてください。',
        nextStep: '「拠点の名前」の欄に入れてから、もう一度「足す」を押してください。',
      });
      return;
    }
    setConfirming({ kind: 'create', name });
  }

  function askRename(site: SiteRow): void {
    if (!editing) return;
    setResult(null);
    const name = editing.name.trim();
    if (name === '' || name === site.name) {
      setResult({
        tone: 'error',
        message:
          name === ''
            ? '名前を変えられませんでした。新しい名前を入れてください。'
            : '名前が変わっていません。',
        nextStep: '新しい名前を入れてから、もう一度「保存する」を押してください。',
      });
      return;
    }
    setConfirming({ kind: 'rename', site, name });
  }

  async function confirm(): Promise<void> {
    if (confirming.kind === 'none') return;
    setBusy(true);
    try {
      if (confirming.kind === 'create') {
        const created = await api.post<SiteRow>('/admin/sites', {
          body: { name: confirming.name },
        });
        setNewName('');
        setResult({
          tone: 'success',
          message:
            created.channel_count > 0
              ? `拠点「${confirming.name}」を足しました。この拠点のトークも${created.channel_count}つ作りました。`
              : `拠点「${confirming.name}」を足しました。`,
          nextStep:
            'この拠点に所属する方は、この拠点のトークに自動で入ります。メンバーの所属は「メンバー」の画面で変えます。',
        });
      } else {
        await api.patch(`/admin/sites/${confirming.site.id}`, { body: { name: confirming.name } });
        setEditing(null);
        setResult({
          tone: 'success',
          message: `拠点「${confirming.site.name}」の名前を「${confirming.name}」に変えました。`,
          nextStep: 'CSVで拠点を書くときは、新しい名前をお使いください。',
        });
      }
      setConfirming({ kind: 'none' });
      setReloadKey((n) => n + 1);
    } catch (caught: unknown) {
      setConfirming({ kind: 'none' });
      const reason = toMessage(caught);
      setResult({
        tone: 'error',
        message: withReason(
          confirming.kind === 'create' ? '拠点を足せませんでした' : '名前を変えられませんでした',
          reason,
        ),
        // 理由の文が次の一手まで言っているとき（「別の名前にしてください。」）は重ねない（#448 L-13）
        ...(endsWithRequest(reason)
          ? {}
          : { nextStep: '名前をご確認のうえ、もう一度お試しください。' }),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article>
      <PageHeader
        title="拠点"
        description="拠点の一覧です。拠点を足す・名前を変えることができます（消すことはできません）。"
      />

      {result ? (
        <ResultBanner
          tone={result.tone}
          message={result.message}
          reveal
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
        />
      ) : null}

      {/* 一覧を読めてから出す（読めない・使えない方に、押しても断られる入力欄を見せない） */}
      {sites ? (
        <FormCard
          title="拠点を足す"
          description="足した拠点は、一覧のいちばん下に入ります。同じ名前の拠点は作れません。拠点を足すと、会社の設定の「拠点のトーク」で決めたトークも一緒に作ります。"
          submitLabel="足す"
          submitBusyLabel="足しています…"
          busy={busy && confirming.kind === 'create'}
          onSubmit={askCreate}
        >
          <label className="ui-field" htmlFor="site-new-name">
            <span>拠点の名前</span>
            <input
              id="site-new-name"
              type="text"
              value={newName}
              maxLength={SITE_NAME_MAX_LENGTH}
              onChange={(event) => setNewName(event.target.value)}
            />
          </label>
        </FormCard>
      ) : null}

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !sites ? <LoadingBlock label="拠点を読み込んでいます…" /> : null}
      {sites && sites.length === 0 ? <EmptyBlock label="拠点はまだありません。" /> : null}
      {sites && sites.length > 0 ? (
        <DataTable label="拠点の一覧">
          <thead>
            <tr>
              <th scope="col">拠点の名前</th>
              <th scope="col">所属している方</th>
              <th scope="col">トーク</th>
              <th scope="col">
                <span className="admin-visually-hidden">操作</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sites.map((site) =>
              editing?.id === site.id ? (
                <tr key={site.id}>
                  <th scope="row" colSpan={3}>
                    <label className="ui-field" htmlFor={`site-rename-${site.id}`}>
                      <span>「{site.name}」の新しい名前</span>
                      <input
                        id={`site-rename-${site.id}`}
                        type="text"
                        value={editing.name}
                        maxLength={SITE_NAME_MAX_LENGTH}
                        onChange={(event) => setEditing({ id: site.id, name: event.target.value })}
                      />
                    </label>
                  </th>
                  <td>
                    <div className="admin-row-actions">
                      <Button
                        variant="primary"
                        compact
                        busy={busy && confirming.kind === 'rename'}
                        onClick={() => askRename(site)}
                      >
                        保存する
                      </Button>
                      <Button
                        variant="secondary"
                        compact
                        disabled={busy}
                        onClick={() => setEditing(null)}
                      >
                        やめる
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={site.id}>
                  <th scope="row" className="ui-cell-wrap">
                    {site.name}
                  </th>
                  <td>{site.member_count.toLocaleString('ja-JP')}名</td>
                  <td>{site.channel_count.toLocaleString('ja-JP')}件</td>
                  <td>
                    <Button
                      variant="secondary"
                      compact
                      disabled={busy || editing !== null}
                      aria-label={`「${site.name}」の名前を変える`}
                      onClick={() => {
                        setResult(null);
                        setEditing({ id: site.id, name: site.name });
                      }}
                    >
                      名前を変える
                    </Button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </DataTable>
      ) : null}

      <ConfirmDialog
        open={confirming.kind !== 'none'}
        title={confirming.kind === 'rename' ? '拠点の名前を変えます' : '拠点を足します'}
        tone="primary"
        message={
          confirming.kind === 'create'
            ? `「${confirming.name}」という拠点を足します。会社の設定の「拠点のトーク」で決めたトークも一緒に作ります。`
            : confirming.kind === 'rename'
              ? `「${confirming.site.name}」の名前を「${confirming.name}」に変えます。所属している方・トークはそのままです。`
              : ''
        }
        reversibility={
          confirming.kind === 'create'
            ? '足した拠点は消せません（名前はあとから変えられます）。足したことは操作履歴に残ります。'
            : '名前はあとから変え直せます。変えたことは操作履歴に残ります。'
        }
        reversible={confirming.kind === 'rename'}
        confirmLabel={confirming.kind === 'rename' ? '名前を変える' : '足す'}
        busy={busy}
        onConfirm={() => void confirm()}
        onCancel={() => setConfirming({ kind: 'none' })}
      />
    </article>
  );
}

function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
